/**
 * Imperative driver for the Explore loading reels (display-only).
 *
 * React renders the cards once (ExploreLoadingSkeleton); this engine moves them. One
 * requestAnimationFrame loop for the whole overlay, CSS transforms only (translateY on
 * pre-drawn strip canvases), the pre-blurred strip swapped in while a reel is moving,
 * `will-change` only on reels that are spinning. The loop pauses while the tab is
 * hidden or the overlay is off screen and stops for good on destroy (overlay unmounted,
 * e.g. the drawer closed or the cards landed).
 *
 * Loading: every card spins and lands on random lines (pickLoadingLanding: never three
 * checks). Finale: the top card lands check-check-check and glows, then a gold halo
 * sweeps down and each loading card flashes and fades as it passes (slotReels timing).
 * prefers-reduced-motion: no spinning; one reel at a time cross-fades to a new symbol,
 * and the finale cross-fades the top card to three checks.
 */
import {
  LOADING_HOLD_MS,
  LOADING_SPIN_MS,
  LOADING_STOP_MS,
  LOADING_STOP_STAGGER_MS,
  PlayingReelFinale,
  REDUCED_SWAP_MS,
  REEL_ACCEL_MS,
  REEL_FINALE_ROWS,
  REEL_KICK_BACK,
  REEL_KICK_MS,
  REEL_ROW_PITCH_PX,
  REEL_SYMBOLS_PER_SEC,
  ReelSymbol,
  WIN_SYMBOL,
  getReelFinaleTiming,
  haloReachesRowMs,
  makeReelStrip,
  pickLoadingLanding,
  pickReelSymbol,
  reelStopDistance,
  reelStopDurationMs,
  reelStopEase,
  reelSymbolsFor,
} from '../../slotReels';
import {
  ReelArtSources,
  drawReelCell,
  drawReelStrip,
  loadReelImage,
  reelArtKey,
  reelImageSources,
  stripOffsetPx,
} from './slotReelArt';
import { SKELETON_CARD_HEIGHT_PX, SKELETON_CARD_MARGIN_PX } from '../../exploreSkeleton';

export type SlotReelEngine = {
  /** Data is ready: play this finale (timed from now). */
  startFinale: (kind: PlayingReelFinale) => void;
  /** Back to loading (a new load started during a finale). */
  resetToLoading: () => void;
  /** The casino (logo) changed. */
  setLogoSrc: (src: string | null) => void;
  destroy: () => void;
};

type ReelState = 'idle' | 'start' | 'spin' | 'stop';

type Reel = {
  el: HTMLElement;
  stripEl: HTMLElement;
  sharp: HTMLCanvasElement;
  blur: HTMLCanvasElement;
  strip: ReelSymbol[];
  state: ReelState;
  pos: number;
  vel: number;
  t0: number;
  p0: number;
  target: number;
  dur: number;
  stopAt: number;
  stopSym: ReelSymbol | null;
  stopMs: number;
  /** Symbol on the payline (reduced mode / after a stop). */
  sym: ReelSymbol;
  /** Reduced mode: which canvas is in front. */
  alt: boolean;
  lastOffset: number;
  moving: boolean;
  spinning: boolean;
};

type CardPhase = 'spin' | 'stopping' | 'hold';

type Card = {
  el: HTMLElement;
  reels: Reel[];
  phase: CardPhase;
  nextAt: number;
  miniUntil: number;
  won: boolean;
  out: boolean;
};

const FIRST_ROW_CENTER_PX = SKELETON_CARD_MARGIN_PX + SKELETON_CARD_HEIGHT_PX / 2;

const rangeMs = (r: Rand, [a, b]: [number, number]): number => (a + r() * (b - a)) / 1000;
type Rand = () => number;

function prefersReducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function createSlotReelEngine(
  root: HTMLElement,
  options: { logoSrc: string | null; rand?: Rand }
): SlotReelEngine {
  const rand: Rand = options.rand || Math.random;
  let art: ReelArtSources = { logoSrc: options.logoSrc };
  let symbols = reelSymbolsFor(art.logoSrc);
  let reduced = prefersReducedMotion();
  let cards: Card[] = [];
  let t = 0;
  let last = -1;
  let raf = 0;
  let alive = true;
  let onScreen = true;
  let finale: PlayingReelFinale | null = null;
  let finaleAt = 0;
  let haloOn = false;
  let drawnArtKey = '';
  // Engine-owned classes go on elements whose className React never rewrites (the cards
  // container, the cards, the halo), never on the root.
  const stage = (root.querySelector<HTMLElement>('.explore-skeleton__cards') || root) as HTMLElement;
  const haloEl = root.querySelector<HTMLElement>('.explore-skeleton__halo');
  let haloDone = false;
  const imageUnsubs: Array<() => void> = [];

  // ---------------------------------------------------------------- setup
  function buildCards(): void {
    const cardEls = Array.from(root.querySelectorAll<HTMLElement>('.slot-reel-card'));
    cards = cardEls.map((el, ci) => {
      el.classList.remove('slot-reel-card--win', 'slot-reel-card--out', 'slot-reel-card--mini');
      const reels = Array.from(el.querySelectorAll<HTMLElement>('.slot-reel')).map((reelEl) => {
        const stripEl = reelEl.querySelector<HTMLElement>('.slot-reel__strip') as HTMLElement;
        const sharp = reelEl.querySelector<HTMLCanvasElement>('.slot-reel__sharp') as HTMLCanvasElement;
        const blur = reelEl.querySelector<HTMLCanvasElement>('.slot-reel__blur') as HTMLCanvasElement;
        const strip = makeReelStrip(symbols, rand);
        const pos = Math.floor(rand() * strip.length);
        const reel: Reel = {
          el: reelEl,
          stripEl,
          sharp,
          blur,
          strip,
          state: 'idle',
          pos,
          vel: 0,
          t0: 0,
          p0: pos,
          target: pos,
          dur: 0,
          stopAt: Infinity,
          stopSym: null,
          stopMs: LOADING_STOP_MS,
          sym: strip[pos],
          alt: false,
          lastOffset: NaN,
          moving: false,
          spinning: false,
        };
        return reel;
      });
      // The starting line is on screen too: never three checks.
      if (reels.length === 3 && reels.every((r) => r.sym === WIN_SYMBOL)) {
        const r = reels[2];
        const L = r.strip.length;
        let p = r.pos;
        do p = (p + 1) % L;
        while (r.strip[p] === WIN_SYMBOL && p !== r.pos);
        r.pos = p;
        r.p0 = p;
        r.target = p;
        r.sym = r.strip[p];
      }
      reels.forEach((r, ri) => {
        // The symbol on the payline once a reel rests (debugging / tests).
        if (reduced) r.el.setAttribute('data-symbol', r.sym);
        else kick(r, t + ci * 0.09 + ri * 0.06);
      });
      return {
        el,
        reels,
        phase: 'spin' as CardPhase,
        nextAt: t + rangeMs(rand, reduced ? REDUCED_SWAP_MS : [1000, 2600]) + ci * 0.15,
        miniUntil: 0,
        won: false,
        out: false,
      };
    });
    stage.classList.toggle('slot-reels--reduced', reduced);
    drawAll();
  }

  function drawAll(): void {
    drawnArtKey = reelArtKey(art);
    cards.forEach((card) =>
      card.reels.forEach((reel) => {
        if (reduced) {
          reel.stripEl.style.transform = '';
          reel.lastOffset = NaN;
          drawReelCell(reel.alt ? reel.blur : reel.sharp, reel.sym, art);
          reel.el.classList.toggle('slot-reel--alt', reel.alt);
          setMoving(reel, false, false);
        } else {
          drawReelStrip(reel.sharp, reel.strip, art, false);
          drawReelStrip(reel.blur, reel.strip, art, true);
          reel.el.classList.remove('slot-reel--alt');
          placeStrip(reel);
        }
      })
    );
  }

  function loadImages(): void {
    reelImageSources(art).forEach((src) => {
      imageUnsubs.push(
        loadReelImage(src, () => {
          if (alive && reelArtKey(art) !== drawnArtKey) drawAll();
        })
      );
    });
  }

  // ---------------------------------------------------------------- reels
  function kick(reel: Reel, at: number): void {
    reel.el.removeAttribute('data-symbol');
    reel.state = 'start';
    reel.t0 = at;
    reel.p0 = reel.pos;
    reel.vel = 0;
    reel.stopAt = Infinity;
    reel.stopSym = null;
  }

  function requestStop(reel: Reel, sym: ReelSymbol, at: number, stopMs: number): void {
    reel.stopSym = sym;
    reel.stopAt = at;
    reel.stopMs = stopMs;
  }

  /** Lands on `stopSym` at the next whole symbol past the stop distance (off screen now). */
  function beginStop(reel: Reel): void {
    const sym = reel.stopSym as ReelSymbol;
    const target = Math.ceil(reel.pos + reelStopDistance(reel.stopMs));
    const L = reel.strip.length;
    const idx = ((target % L) + L) % L;
    if (reel.strip[idx] !== sym) {
      reel.strip[idx] = sym;
      drawReelStrip(reel.sharp, reel.strip, art, false);
      drawReelStrip(reel.blur, reel.strip, art, true);
    }
    reel.state = 'stop';
    reel.p0 = reel.pos;
    reel.t0 = t;
    reel.target = target;
    reel.dur = reelStopDurationMs(target - reel.pos) / 1000;
    reel.stopAt = Infinity;
  }

  function updateReel(reel: Reel, dt: number): void {
    if (reel.state === 'start') {
      const e = t - reel.t0;
      if (e < 0) return;
      const kickS = REEL_KICK_MS / 1000;
      if (e < kickS) {
        const k = e / kickS;
        reel.pos = reel.p0 - REEL_KICK_BACK * (1 - (1 - k) * (1 - k) * (1 - k));
        return;
      }
      reel.vel = Math.min(REEL_SYMBOLS_PER_SEC, reel.vel + (REEL_SYMBOLS_PER_SEC / (REEL_ACCEL_MS / 1000)) * dt);
      reel.pos += reel.vel * dt;
      if (reel.vel >= REEL_SYMBOLS_PER_SEC) reel.state = 'spin';
      return;
    }
    if (reel.state === 'spin') {
      reel.vel = REEL_SYMBOLS_PER_SEC;
      reel.pos += reel.vel * dt;
      if (reel.stopSym && t >= reel.stopAt) beginStop(reel);
      return;
    }
    if (reel.state === 'stop') {
      const u = reel.dur > 0 ? (t - reel.t0) / reel.dur : 1;
      if (u >= 1) {
        const L = reel.strip.length;
        reel.pos = ((reel.target % L) + L) % L;
        reel.sym = reel.strip[reel.pos];
        reel.state = 'idle';
        reel.vel = 0;
        reel.el.setAttribute('data-symbol', reel.sym);
      } else {
        reel.pos = reel.p0 + (reel.target - reel.p0) * reelStopEase(u);
      }
    }
  }

  function setMoving(reel: Reel, moving: boolean, spinning: boolean): void {
    if (reel.moving !== moving) {
      reel.moving = moving;
      reel.el.classList.toggle('slot-reel--moving', moving);
    }
    if (reel.spinning !== spinning) {
      reel.spinning = spinning;
      reel.el.classList.toggle('slot-reel--spinning', spinning);
    }
  }

  function placeStrip(reel: Reel): void {
    const offset = Math.round(stripOffsetPx(reel.pos, reel.strip.length) * 4) / 4;
    if (offset !== reel.lastOffset) {
      reel.lastOffset = offset;
      reel.stripEl.style.transform = `translate3d(0, ${offset}px, 0)`;
    }
    const moving =
      reel.state === 'spin' ||
      (reel.state === 'start' && reel.vel > 5) ||
      (reel.state === 'stop' && (t - reel.t0) / reel.dur < 0.45);
    setMoving(reel, moving, reel.state !== 'idle');
  }

  // ---------------------------------------------------------------- cards
  function updateLoadingCard(card: Card): void {
    const allIdle = card.reels.every((r) => r.state === 'idle');
    if (card.phase === 'spin' && t >= card.nextAt && card.reels.every((r) => r.state === 'spin')) {
      const line = pickLoadingLanding(symbols, rand);
      card.reels.forEach((r, i) => requestStop(r, line[i], t + (i * LOADING_STOP_STAGGER_MS) / 1000, LOADING_STOP_MS));
      card.phase = 'stopping';
    } else if (card.phase === 'stopping' && allIdle) {
      card.phase = 'hold';
      card.nextAt = t + rangeMs(rand, LOADING_HOLD_MS);
      const s0 = card.reels[0].sym;
      if (card.reels.every((r) => r.sym === s0)) {
        card.el.classList.add('slot-reel-card--mini');
        card.miniUntil = t + 0.7;
      }
    } else if (card.phase === 'hold' && t >= card.nextAt) {
      card.reels.forEach((r, i) => kick(r, t + i * 0.07));
      card.phase = 'spin';
      card.nextAt = t + rangeMs(rand, LOADING_SPIN_MS);
    }
    if (card.miniUntil && t >= card.miniUntil) {
      card.miniUntil = 0;
      card.el.classList.remove('slot-reel-card--mini');
    }
  }

  /** Reduced motion: one reel cross-fades to a new symbol now and then (never ✓✓✓). */
  function updateReducedCard(card: Card): void {
    if (t < card.nextAt) return;
    const reel = card.reels[Math.floor(rand() * card.reels.length)];
    let sym = pickReelSymbol(symbols, rand, reel.sym);
    const othersChecked = card.reels.filter((r) => r !== reel && r.sym === WIN_SYMBOL).length === 2;
    if (sym === WIN_SYMBOL && othersChecked) sym = pickReelSymbol(symbols, rand, WIN_SYMBOL);
    crossFade(reel, sym);
    card.nextAt = t + rangeMs(rand, REDUCED_SWAP_MS);
  }

  function crossFade(reel: Reel, sym: ReelSymbol): void {
    reel.sym = sym;
    reel.el.setAttribute('data-symbol', sym);
    reel.alt = !reel.alt;
    drawReelCell(reel.alt ? reel.blur : reel.sharp, sym, art);
    reel.el.classList.toggle('slot-reel--alt', reel.alt);
  }

  // ---------------------------------------------------------------- finale
  function finaleElapsedMs(): number {
    return (t - finaleAt) * 1000;
  }

  function updateFinale(): void {
    if (!finale) return;
    const kind = finale;
    const timing = getReelFinaleTiming(kind);
    const e = finaleElapsedMs();
    const top = cards[0];

    if (top && !top.won) {
      if (reduced) {
        top.reels.forEach((r, i) => {
          if (e >= timing.topStopAtMs[i] && r.sym !== WIN_SYMBOL) crossFade(r, WIN_SYMBOL);
        });
        if (top.reels.every((r) => r.sym === WIN_SYMBOL)) winTop(top);
      } else if (top.reels.every((r) => r.state === 'idle' && r.sym === WIN_SYMBOL)) {
        winTop(top);
      }
    }

    // Halo + cards leaving, top to bottom.
    if (e >= timing.haloStartMs) {
      if (!haloOn) {
        haloOn = true;
        if (haloEl) haloEl.classList.add('explore-skeleton__halo--on');
      }
      if (haloEl && timing.haloRowStepMs > 0) {
        const rows = (e - timing.haloStartMs) / timing.haloRowStepMs - 1;
        // Centre of card `rows` (cards are 80px with 8px margins, so the first centre is at 48px).
        const y = REEL_ROW_PITCH_PX * rows + FIRST_ROW_CENTER_PX;
        haloEl.style.transform = `translate3d(0, ${Math.round(y)}px, 0)`;
        if (rows > REEL_FINALE_ROWS + 1 && !haloDone) {
          haloDone = true;
          haloEl.classList.add('explore-skeleton__halo--done');
        }
      }
      cards.forEach((card, i) => {
        if (!card.out && e >= haloReachesRowMs(kind, i)) {
          card.out = true;
          card.el.classList.add('slot-reel-card--out');
        }
      });
    }
  }

  function resetHalo(): void {
    haloOn = false;
    haloDone = false;
    if (haloEl) {
      haloEl.classList.remove('explore-skeleton__halo--on', 'explore-skeleton__halo--done');
      haloEl.style.transform = '';
    }
  }

  function winTop(top: Card): void {
    top.won = true;
    top.el.classList.remove('slot-reel-card--mini');
    top.el.classList.add('slot-reel-card--win');
  }

  // ---------------------------------------------------------------- loop
  function frame(now: number): void {
    raf = 0;
    if (!alive) return;
    const dt = last < 0 ? 0 : Math.min(0.05, (now - last) / 1000);
    last = now;
    t += dt;
    cards.forEach((card, ci) => {
      if (reduced) {
        if (!(finale && ci === 0) && !card.out) updateReducedCard(card);
        return;
      }
      card.reels.forEach((r) => updateReel(r, dt));
      if (!(finale && ci === 0) && !card.out) updateLoadingCard(card);
      card.reels.forEach(placeStrip);
    });
    updateFinale();
    const finished =
      finale !== null &&
      cards.every((c) => c.out) &&
      (haloDone || reduced || getReelFinaleTiming(finale).haloRowStepMs === 0);
    if (finished) {
      cards.forEach((c) => c.reels.forEach((r) => setMoving(r, false, false)));
      return; // nothing left to animate; CSS finishes the fades
    }
    schedule();
  }

  function schedule(): void {
    if (!alive || raf || !onScreen) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    raf = window.requestAnimationFrame(frame);
  }

  function pause(): void {
    if (raf) window.cancelAnimationFrame(raf);
    raf = 0;
    last = -1;
  }

  const onVisibility = (): void => {
    if (document.hidden) pause();
    else schedule();
  };
  document.addEventListener('visibilitychange', onVisibility);

  let io: IntersectionObserver | null = null;
  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      onScreen = !!entry && entry.isIntersecting;
      if (onScreen) schedule();
      else pause();
    });
    io.observe(root);
  }

  buildCards();
  loadImages();
  schedule();

  return {
    startFinale(kind) {
      if (!alive) return;
      if (kind === 'reduced' && !reduced) {
        reduced = true;
        buildCards();
      }
      finale = kind;
      finaleAt = t;
      resetHalo();
      stage.style.setProperty('--reel-out-ms', `${getReelFinaleTiming(kind).cardOutMs}ms`);
      const top = cards[0];
      if (top && !reduced) {
        const timing = getReelFinaleTiming(kind);
        top.el.classList.remove('slot-reel-card--mini');
        top.reels.forEach((r, i) => {
          if (r.state !== 'spin') {
            r.state = 'spin';
            r.vel = REEL_SYMBOLS_PER_SEC;
            r.el.removeAttribute('data-symbol');
          }
          requestStop(r, WIN_SYMBOL, t + timing.topStopAtMs[i] / 1000, timing.stopMs);
        });
      }
      schedule();
    },
    resetToLoading() {
      if (!alive || finale === null) return;
      finale = null;
      resetHalo();
      reduced = prefersReducedMotion();
      buildCards();
      schedule();
    },
    setLogoSrc(src) {
      const next = src || null;
      if (!alive || next === art.logoSrc) return;
      art = { logoSrc: next };
      symbols = reelSymbolsFor(next);
      if (finale === null) buildCards();
      loadImages();
      schedule();
    },
    destroy() {
      alive = false;
      pause();
      document.removeEventListener('visibilitychange', onVisibility);
      if (io) io.disconnect();
      imageUnsubs.forEach((u) => u());
    },
  };
}
